import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { pick } from 'lodash'
import { IStepComponentProps } from '../../types/IStepComponentProps'
import Form, { FormRow } from '../../../../../components/common/form/Form'
import {
  ButtonPrimary,
  Input,
  Switch,
  Select,
  Box,
  ButtonOutline
} from 'front-end/src/components'

import { UsersImportStep } from '../../../types/UserImportStep'
import { UsersCsvImportConfigurationPatch } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { useUsersImportDetailsQueryWithParams } from '../../UsersImportDetails'
import { useAllGroupsQuery } from 'front-end/src/graphql/commonQueries/allGroups.codegen'
import { usePatchUsersImportMutation } from '../../operations/patchUsersImport.codegen'
import {
  useSetImportMappingTitleMutation,
  useProcessUserImportMutation
} from './SettingsStepComponent.codegen'
import { NbSp } from 'front-end/src/utils/NbSp'

export const SettingsStepComponent: FunctionComponent<IStepComponentProps> = ({
  usersImport,
  setStep
}) => {
  // Should be part of render to get correct translations
  const DEFAULT_GROUP_OPTIONS = [{ value: '', label: t`None` }]
  const { refetch } = useUsersImportDetailsQueryWithParams()

  const [configurationPatch, setConfigurationPatch] =
    useState<UsersCsvImportConfigurationPatch>(
      pick(usersImport.configuration, 'updateExisting', 'groupId')
    )

  const onBack = useCallback(() => {
    setStep(UsersImportStep.FIELDS)
  }, [setStep])

  const onConfigurationPatchChanged = useCallback(
    (field: keyof UsersCsvImportConfigurationPatch, value: any) => {
      setConfigurationPatch({ ...configurationPatch, [field]: value })
    },
    [configurationPatch, setConfigurationPatch]
  )

  const { data: groups } = useAllGroupsQuery()

  const groupOptions = useMemo(() => {
    if (!groups) {
      return DEFAULT_GROUP_OPTIONS
    }

    return [
      ...DEFAULT_GROUP_OPTIONS,
      ...groups.groups.map(({ id: value, name: caption }) => ({
        value,
        label: caption
      }))
    ]
  }, [groups])

  const canSaveMapping = useMemo(
    () => !usersImport.importMapping?.title,
    [usersImport.importMapping]
  )

  const [newMappingTitle, setNewMappingTitle] = useState(
    usersImport.importMapping?.title
  )

  const [patchUsersImportMutation, { loading: savingUsersImport }] =
    usePatchUsersImportMutation()

  const [setMappingTitleMutation, { loading: savingImportMapping }] =
    useSetImportMappingTitleMutation()

  const [processUsersImportMutation, { loading: startingProcessing }] =
    useProcessUserImportMutation()

  const onSubmit = useCallback(async () => {
    await patchUsersImportMutation({
      variables: {
        id: usersImport.id,
        configuration: configurationPatch
      }
    })

    if (newMappingTitle) {
      await setMappingTitleMutation({
        variables: {
          id: usersImport.importMapping?.id as number, // we know it's present on this step
          title: newMappingTitle
        }
      })
    }

    await processUsersImportMutation({
      variables: { id: usersImport.id }
    })
    refetch()
  }, [
    patchUsersImportMutation,
    setMappingTitleMutation,
    processUsersImportMutation,
    newMappingTitle,
    configurationPatch,
    usersImport
  ])

  return (
    <div className="BulkUsersImportPage__step settings-step">
      <h3>
        <Trans>Confirm your import settings</Trans>
      </h3>
      <Form onSubmit={onSubmit}>
        <FormRow>
          <span>
            <h4>
              <Trans>Update existing users</Trans>
            </h4>
            <p className="help-text">
              <Trans>
                If selected, any existing users whose email matches the imported
                data will have their details updated with the imported details.
                If unselected, existing users will be skipped.
              </Trans>
            </p>
          </span>
          <Switch
            isChecked={!!configurationPatch.updateExisting}
            onChange={() =>
              onConfigurationPatchChanged(
                'updateExisting',
                !configurationPatch.updateExisting
              )
            }
          />
        </FormRow>
        <FormRow>
          <Box>
            <h4>
              <Trans>Add all users to a single group</Trans>
            </h4>
            <p className="help-text">
              <Trans>
                Select a group and all users from this import will be added into
                the group. Users who are already members of
                <NbSp />
                the
                <NbSp />
                group will not be
                <NbSp />
                effected.
              </Trans>
            </p>
          </Box>
          <Box width="100%">
            <Select
              options={groupOptions}
              onChange={(v) => {
                const groupId = v?.value ? parseInt(v?.value, 10) : undefined
                onConfigurationPatchChanged('groupId', groupId)
              }}
              value={groupOptions.find(({ value }) => {
                return value === configurationPatch.groupId
              })}
            />
          </Box>
        </FormRow>
        {canSaveMapping && (
          <FormRow className="save-settings">
            <span>
              <h4>
                <Trans>Save settings to a new field map</Trans>
              </h4>
              <p>
                <Trans>
                  Save your field mappings and import settings to use again in
                  the future.
                </Trans>
              </p>
            </span>
            <Input
              value={newMappingTitle || ''}
              onChange={(ev) => {
                setNewMappingTitle(ev.currentTarget.value)
              }}
              placeholder={t`New field map`}
            />
          </FormRow>
        )}

        <p>
          <Trans>
            Once you are confident that your fields are correctly matched and
            you have selected your import settings, click on Import user details
            to trigger the data import.
            <b>This action cannot be undone!</b>
          </Trans>
        </p>
      </Form>
      <div className="BulkUsersImportPage__step-actions">
        <ButtonOutline onClick={onBack}>
          <Trans>Back</Trans>
        </ButtonOutline>
        <ButtonPrimary
          onClick={onSubmit}
          disabled={
            savingUsersImport || savingImportMapping || startingProcessing
          }
        >
          <Trans>Import user details</Trans>
        </ButtonPrimary>
      </div>
    </div>
  )
}
